/**
 * Vista Kanban — colunas CONFIGURÁVEIS (por unidade) lado a lado, com
 * drag-and-drop (HTML5 nativo, mesma mecânica do board do CRM). Cada coluna
 * agrupa uma ou mais situações; arrastar um card para outra coluna dispara a
 * transição correspondente (via `onMover`), respeitando a máquina de estados:
 * ABERTA→AUTORIZADA (autorizar), AUTORIZADA→EM_ANDAMENTO (iniciar),
 * AUTORIZADA/EM_ANDAMENTO→ATENDIDA (atender — sem retirada completa, a tela
 * abre o registro da retirada antes), e ABERTA/AUTORIZADA/EM_ANDAMENTO→
 * (coluna com RECUSADA/CANCELADA) = encerrar (modal de motivo no container).
 * Cards terminais não arrastam; só colunas com transição válida aceitam o drop.
 *
 * A tela principal NÃO rola: o board mede a própria posição e ocupa a altura
 * restante; o scroll vertical fica dentro de cada coluna (regra da gestora).
 */
import React, { useLayoutEffect, useRef, useState } from "react";
import { FiCamera } from "react-icons/fi";

import {
  chaveSolicitacao,
  contagemItens,
  dataHora,
  SELECAO_SO_LOTE,
} from "./helpers";
import PrazoChip from "./PrazoChip";
import {
  Board,
  Checkbox,
  KCard,
  KCardCliente,
  KCardMeta,
  KCardSolicitante,
  KCardTop,
  Lane,
  LaneBody,
  LaneCount,
  LaneEmpty,
  LaneHead,
  LaneTitle,
  LoteTag,
  LotesWrap,
  Pill,
} from "./styles";

const TERMINAIS = new Set(["ATENDIDA", "RECUSADA", "CANCELADA"]);
// Situações que só ficam em coluna que as ANCORA (espelha
// `solicitacoes.kanban.SITUACOES_TRAVADAS_NA_COLUNA` do backend): sem a
// autorização do gestor o card não avança; recusada/cancelada só reabrindo.
const ENCERRADAS_SEM_ATENDER = new Set(["RECUSADA", "CANCELADA"]);

/**
 * Transição válida ao soltar um card (situação de origem) numa coluna que
 * agrupa `situacoesDaColuna`. Retorna "autorizar" | "iniciar" | "atender" |
 * "encerrar" | "reabrir" ou null. Espelha a máquina de estados do backend.
 * `reabrir`: card FINALIZADO (Atendida/Recusada/Cancelada) solto numa coluna
 * que ancora um status ATIVO — volta ao fluxo (backend `reabrir`).
 */
export function transicaoParaColuna(situacao, situacoesDaColuna) {
  const set = new Set(situacoesDaColuna || []);
  if (set.has(situacao)) return null; // já está nesta coluna
  // Coluna operacional (sem situação): card aguardando autorização só entra
  // AUTORIZANDO — o quadro não pode exibir "Aguardando Entrega" para algo
  // que o gestor ainda não aprovou.
  if (set.size === 0) return situacao === "ABERTA" ? "autorizar" : null;
  if (situacao === "ABERTA" && set.has("AUTORIZADA")) return "autorizar";
  if (situacao === "AUTORIZADA" && set.has("EM_ANDAMENTO")) return "iniciar";
  if (
    (situacao === "AUTORIZADA" || situacao === "EM_ANDAMENTO") &&
    set.has("ATENDIDA")
  ) {
    return "atender";
  }
  if (
    (situacao === "ABERTA" ||
      situacao === "AUTORIZADA" ||
      situacao === "EM_ANDAMENTO") &&
    (set.has("RECUSADA") || set.has("CANCELADA"))
  ) {
    return "encerrar";
  }
  // Card finalizado arrastado para uma coluna com status ATIVO = reabrir.
  if (
    TERMINAIS.has(situacao) &&
    [...set].some((s) => !TERMINAIS.has(s))
  ) {
    return "reabrir";
  }
  return null;
}

// Situação-alvo da reabertura = 1º status não-terminal ancorado pela coluna.
export function situacaoAlvoReabertura(situacoesDaColuna) {
  return (situacoesDaColuna || []).find((s) => !TERMINAIS.has(s)) || null;
}

/**
 * Em qual COLUNA o card aparece (modelo desacoplado da Fase 2):
 * 1) se tem `etapa_quadro` e essa coluna existe → nela;
 * 2) senão, na 1ª coluna que ANCORA a situação atual (compat);
 * 3) senão, em nenhuma (fica escondido).
 */
export function colunaDoCard(card, colunas) {
  const lista = colunas || [];
  if (card?.etapa_quadro) {
    const c = lista.find((col) => col.id === card.etapa_quadro);
    if (c) return c;
  }
  return (
    lista.find((col) => (col.situacoes || []).includes(card.situacao)) || null
  );
}

/**
 * Pode soltar `card` na `coluna`? (diferente da coluna atual E:) coluna
 * operacional pura (sem situação), OU mesma situação em outra coluna (mover),
 * OU transição de status válida. Status-coluna sem transição válida bloqueia.
 */
export function podeSoltarNaColuna(card, coluna, colunas) {
  const atual = colunaDoCard(card, colunas);
  if (atual && atual.id === coluna.id) return false;
  const sits = coluna.situacoes || [];
  if (sits.includes(card.situacao)) return true;
  if (sits.length === 0 && card.situacao !== "ABERTA") {
    return !ENCERRADAS_SEM_ATENDER.has(card.situacao);
  }
  return !!transicaoParaColuna(card.situacao, sits);
}

export default function Kanban({
  itens,
  onAbrir,
  onMover,
  podeMover = false,
  colunas = [],
  // Seleção (lote MDF e/ou junção) — mesma regra e mesmo conjunto da vista
  // Tabela; sem `selecao`, vale só a regra do lote.
  mostrarSelecao = false,
  selecionadas,
  onToggleSelecao,
  selecao = SELECAO_SO_LOTE,
}) {
  const boardRef = useRef(null);
  const [alturaBoard, setAlturaBoard] = useState(null);

  const [dragChave, setDragChave] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  useLayoutEffect(() => {
    const medir = () => {
      if (!boardRef.current) return;
      const topo = boardRef.current.getBoundingClientRect().top + window.scrollY;
      const base = Math.max(320, window.innerHeight - topo - 24);
      setAlturaBoard(base);
      requestAnimationFrame(() => {
        const sobra =
          document.documentElement.scrollHeight - window.innerHeight;
        if (sobra > 0) setAlturaBoard(Math.max(320, base - sobra));
      });
    };
    medir();
    window.addEventListener("resize", medir);
    return () => window.removeEventListener("resize", medir);
  }, []);

  // Todos os cards arrastam (inclusive finalizados — pra reabrir/mover).
  const arrastavel = () => podeMover;

  const cardDoDrag = () =>
    itens.find((s) => chaveSolicitacao(s) === dragChave) || null;

  const aoIniciarDrag = (e, s) => {
    if (!arrastavel(s)) return;
    setDragChave(chaveSolicitacao(s));
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", chaveSolicitacao(s));
  };

  const aoFinalizarDrag = () => {
    setDragChave(null);
    setDragOverCol(null);
  };

  const aoArrastarSobre = (e, coluna) => {
    const sol = cardDoDrag();
    if (!sol || !podeSoltarNaColuna(sol, coluna, colunas)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverCol !== coluna.id) setDragOverCol(coluna.id);
  };

  const aoSair = () => setDragOverCol(null);

  const aoSoltar = (e, coluna) => {
    e.preventDefault();
    const chave = dragChave || e.dataTransfer.getData("text/plain");
    aoFinalizarDrag();
    if (!chave) return;
    const sol = itens.find((s) => chaveSolicitacao(s) === chave);
    if (!sol || !podeSoltarNaColuna(sol, coluna, colunas)) return;
    onMover?.(sol, coluna);
  };

  return (
    <Board
      ref={boardRef}
      style={alturaBoard ? { height: `${alturaBoard}px` } : undefined}
    >
      {colunas.map((coluna) => {
        const daColuna = itens.filter(
          (s) => colunaDoCard(s, colunas)?.id === coluna.id,
        );
        const solDrag = cardDoDrag();
        const alvoValido =
          !!solDrag && podeSoltarNaColuna(solDrag, coluna, colunas);
        const over = dragOverCol === coluna.id;
        const destaque = over
          ? {
              outline: "2px dashed var(--accent)",
              outlineOffset: "-2px",
              background: "var(--accent-soft)",
            }
          : alvoValido
            ? { outline: "1px dashed var(--accent)", outlineOffset: "-2px" }
            : undefined;
        return (
          <Lane
            key={coluna.id}
            style={destaque}
            onDragOver={(e) => aoArrastarSobre(e, coluna)}
            onDragLeave={aoSair}
            onDrop={(e) => aoSoltar(e, coluna)}
          >
            <LaneHead>
              <LaneTitle>
                {coluna.cor && (
                  <span
                    aria-hidden="true"
                    style={{
                      display: "inline-block",
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      background: coluna.cor,
                      marginRight: 7,
                      verticalAlign: "middle",
                    }}
                  />
                )}
                {coluna.titulo}
              </LaneTitle>
              <LaneCount>{daColuna.length}</LaneCount>
            </LaneHead>
            <LaneBody>
              {daColuna.length === 0 ? (
                <LaneEmpty>Nenhuma solicitação</LaneEmpty>
              ) : (
                daColuna.map((s) => {
                  const podeArrastar = arrastavel(s);
                  return (
                    <KCard
                      key={chaveSolicitacao(s)}
                      draggable={podeArrastar}
                      onDragStart={(e) => aoIniciarDrag(e, s)}
                      onDragEnd={aoFinalizarDrag}
                      onClick={() => onAbrir(s)}
                      data-selecionado={selecionadas?.has(chaveSolicitacao(s)) || undefined}
                      style={
                        dragChave === chaveSolicitacao(s)
                          ? { opacity: 0.5, cursor: "grabbing" }
                          : podeArrastar
                            ? { cursor: "grab" }
                            : undefined
                      }
                      title={
                        podeArrastar
                          ? "Clique para abrir; arraste para mudar a situação"
                          : "Clique para abrir"
                      }
                    >
                      <KCardTop>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                          {/* A caixa surge no hover do card (e fica visível
                              enquanto marcada) para não poluir o quadro. */}
                          {mostrarSelecao && (
                            <span
                              className="ksel"
                              data-on={selecionadas?.has(chaveSolicitacao(s)) || undefined}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Checkbox
                                checked={!!selecionadas?.has(chaveSolicitacao(s))}
                                disabled={!selecao.pode(s)}
                                title={selecao.titulo(s)}
                                onChange={() => onToggleSelecao?.(s)}
                                aria-label={`Selecionar ${s.numero}`}
                              />
                            </span>
                          )}
                          <strong>{s.numero}</strong>
                        </span>
                        <Pill $tone="neutral">{s.tipo}</Pill>
                      </KCardTop>
                      <KCardCliente title={s.cliente_nome || ""}>
                        {s.cliente_nome || "Sem cliente"}
                      </KCardCliente>
                      <KCardSolicitante>
                        <span className="quem" title={s.criado_por_nome || ""}>
                          {s.criado_por_nome || "—"}
                        </span>
                        <span className="quando">{dataHora(s.created_at)}</span>
                      </KCardSolicitante>
                      <KCardMeta>
                        <span>{contagemItens(s)}</span>
                        {s.tem_fotos && (
                          <FiCamera
                            size={12}
                            title="Possui fotos"
                            aria-label="Possui fotos"
                          />
                        )}
                      </KCardMeta>
                      <PrazoChip solicitacao={s} style={{ marginTop: 6, alignSelf: "flex-start" }} />
                      {(s.lotes_numeros || []).length > 0 && (
                        <LotesWrap style={{ marginTop: 6 }}>
                          {s.lotes_numeros.map((num) => (
                            <LoteTag key={num}>LOTE {num}</LoteTag>
                          ))}
                        </LotesWrap>
                      )}
                    </KCard>
                  );
                })
              )}
            </LaneBody>
          </Lane>
        );
      })}
    </Board>
  );
}
